// DealAutomationEngine.js - Handle stage-based automation for deals
import toast from 'react-hot-toast'
import eventBus, { EventTypes } from './EventBus'
import FirebaseUserDataService from './firebaseUserData'
import { getFunctions, httpsCallable } from 'firebase/functions'

class DealAutomationEngine {
  /**
   * Handle deal stage change and trigger appropriate automations
   * @param {object} deal - The deal that was moved
   * @param {string} oldStage - Previous stage ID
   * @param {string} newStage - New stage ID
   * @param {string} tenantId - Tenant ID
   */
  async handleStageChange(deal, oldStage, newStage, tenantId) {
    console.log(`🎯 Deal stage changed: ${oldStage} → ${newStage}`, deal)

    try {
      // Emit event for workflow triggers
      eventBus.emit(EventTypes.DEAL_STAGE_CHANGED, {
        dealId: deal.id,
        dealTitle: deal.title || `${deal.companyName} - ${deal.contactName}`,
        oldStage,
        newStage,
        dealValue: deal.dealValue,
        probability: deal.probability,
        contactEmail: deal.contactEmail,
        companyName: deal.companyName
      })

      // Execute stage-specific automations
      switch (newStage) {
        case 'prospects':
          await this.handleProspectsStage(deal, tenantId)
          break
        case 'qualified':
          await this.handleQualifiedStage(deal, tenantId)
          break
        case 'proposal':
          await this.handleProposalStage(deal, tenantId)
          break
        case 'negotiation':
          await this.handleNegotiationStage(deal, tenantId)
          break
        case 'closed_won':
          await this.handleClosedWonStage(deal, tenantId)
          break
        case 'closed_lost':
          await this.handleClosedLostStage(deal, tenantId)
          break
      }

      // Check for stuck deals in all stages
      this.scheduleStuckDealCheck(deal, tenantId)

    } catch (error) {
      console.error('Error in deal automation:', error)
      toast.error('Automation failed: ' + error.message)
    }
  }

  /**
   * PROSPECTS Stage Automation
   * - Send introduction email
   * - Set follow-up reminder
   * - Update probability to 30%
   */
  async handleProspectsStage(deal, tenantId) {
    console.log('🎯 Executing Prospects stage automation...')

    // Update deal probability
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      probability: 30,
      nextFollowUp: this.addDaysToDate(new Date(), 3).toISOString() // Follow up in 3 days
    })

    // Send introduction email
    if (deal.contactEmail) {
      const emailSent = await this.sendStageEmail(deal, 'introduction', tenantId)
      if (emailSent) {
        toast.success(`📧 Introduction email sent to ${deal.contactName}`)
      }
    }

    toast('✅ Deal added to Prospects pipeline', { icon: '🎯' })
  }

  /**
   * QUALIFIED Stage Automation
   * - Send product brochure
   * - Generate demo booking link
   * - Update probability to 50%
   * - Notify sales team
   */
  async handleQualifiedStage(deal, tenantId) {
    console.log('✅ Executing Qualified stage automation...')

    // Update deal probability
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      probability: 50,
      nextFollowUp: this.addDaysToDate(new Date(), 2).toISOString()
    })

    // Send qualification email with brochure
    if (deal.contactEmail) {
      const emailSent = await this.sendStageEmail(deal, 'qualified', tenantId)
      if (emailSent) {
        toast.success(`📧 Product brochure sent to ${deal.contactName}`)
      }
    }

    // High-value deal notification
    if (deal.dealValue >= 50000) {
      toast(`💰 High-value deal qualified: ${deal.companyName} - $${deal.dealValue.toLocaleString()}!`, {
        icon: '🔥',
        duration: 5000
      })
    }

    toast.success('✅ Deal qualified - automation triggered')
  }

  /**
   * PROPOSAL Stage Automation
   * - Send proposal email
   * - Set follow-up for 2 days
   * - Update probability to 65%
   * - Track proposal opens
   */
  async handleProposalStage(deal, tenantId) {
    console.log('📄 Executing Proposal stage automation...')

    // Update deal probability
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      probability: 65,
      proposalsSent: (deal.proposalsSent || 0) + 1,
      nextFollowUp: this.addDaysToDate(new Date(), 2).toISOString()
    })

    // Send proposal email
    if (deal.contactEmail) {
      const emailSent = await this.sendStageEmail(deal, 'proposal', tenantId)
      if (emailSent) {
        toast.success(`📄 Proposal sent to ${deal.contactName}`)
      }
    }

    toast('📄 Proposal sent - follow-up scheduled', { icon: '✅' })
  }

  /**
   * NEGOTIATION Stage Automation
   * - Send case studies
   * - Update probability to 75%
   * - Alert for high-value deals
   * - Schedule contract review
   */
  async handleNegotiationStage(deal, tenantId) {
    console.log('🤝 Executing Negotiation stage automation...')

    // Update deal probability
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      probability: 75,
      nextFollowUp: this.addDaysToDate(new Date(), 1).toISOString() // Daily follow-up
    })

    // Send negotiation support email
    if (deal.contactEmail) {
      const emailSent = await this.sendStageEmail(deal, 'negotiation', tenantId)
      if (emailSent) {
        toast.success(`🤝 Case studies sent to ${deal.contactName}`)
      }
    }

    // Alert for deals needing approval
    if (deal.dealValue >= 100000) {
      toast('🚨 Enterprise deal in negotiation - manager approval may be needed', {
        icon: '💼',
        duration: 6000
      })
    }

    toast.success('🤝 Negotiation started - support materials sent')
  }

  /**
   * CLOSED WON Stage Automation
   * - Send thank you email
   * - Trigger onboarding workflow
   * - Update to customer status
   * - Celebrate the win!
   */
  async handleClosedWonStage(deal, tenantId) {
    console.log('🎉 Executing Closed Won automation...')

    // Update deal to 100% probability
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      probability: 100,
      closedAt: new Date().toISOString()
    })

    // Send thank you email
    if (deal.contactEmail) {
      const emailSent = await this.sendStageEmail(deal, 'closed_won', tenantId)
      if (emailSent) {
        toast.success(`🎉 Thank you email sent to ${deal.contactName}`)
      }
    }

    // Emit event for onboarding workflow
    eventBus.emit(EventTypes.DEAL_WON, {
      dealId: deal.id,
      contactEmail: deal.contactEmail,
      companyName: deal.companyName,
      dealValue: deal.dealValue
    })

    // Celebration notification
    toast.success(`🎉 Deal Won! ${deal.companyName} - $${deal.dealValue.toLocaleString()}`, {
      duration: 6000,
      icon: '🏆'
    })

    // Play celebration sound (optional)
    this.playCelebrationSound()
  }

  /**
   * CLOSED LOST Stage Automation
   * - Add to re-engagement workflow (6 months)
   * - Send feedback survey
   * - Archive deal
   * - Update statistics
   */
  async handleClosedLostStage(deal, tenantId) {
    console.log('😞 Executing Closed Lost automation...')

    // Update deal probability to 0
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      probability: 0,
      closedAt: new Date().toISOString()
    })

    // Schedule re-engagement in 6 months
    const reEngageDate = this.addMonthsToDate(new Date(), 6)
    await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
      reEngageAt: reEngageDate.toISOString()
    })

    // Emit event for re-engagement workflow
    eventBus.emit(EventTypes.DEAL_LOST, {
      dealId: deal.id,
      contactEmail: deal.contactEmail,
      companyName: deal.companyName,
      lostReason: deal.lostReason
    })

    toast(`Deal closed - re-engagement scheduled for ${reEngageDate.toLocaleDateString()}`, {
      icon: 'ℹ️',
      duration: 5000
    })
  }

  /**
   * Send stage-specific email
   * @param {object} deal - Deal object
   * @param {string} stageType - Type of email (introduction, qualified, proposal, etc.)
   * @param {string} tenantId - Tenant ID
   */
  async sendStageEmail(deal, stageType, tenantId) {
    try {
      // Email templates based on stage
      const templates = {
        introduction: {
          subject: `Nice to meet you, ${deal.contactName}!`,
          content: `Hi ${deal.contactName},\n\nThank you for your interest in our services. I wanted to reach out and introduce myself.\n\nI'd love to learn more about ${deal.companyName} and see how we can help you achieve your goals.\n\nWould you be open to a quick 15-minute call this week?\n\nBest regards`
        },
        qualified: {
          subject: `Product Information for ${deal.companyName}`,
          content: `Hi ${deal.contactName},\n\nGreat speaking with you! As promised, here's more information about our solutions.\n\n[Product Brochure Link]\n\nI think this would be perfect for ${deal.companyName}. Let me know if you'd like to schedule a demo!\n\nBest regards`
        },
        proposal: {
          subject: `Proposal for ${deal.companyName}`,
          content: `Hi ${deal.contactName},\n\nAttached is our proposal tailored specifically for ${deal.companyName}.\n\nKey highlights:\n• Custom solution for your needs\n• Estimated value: $${deal.dealValue.toLocaleString()}\n• 90-day implementation timeline\n\nLet's schedule a call to discuss!\n\nBest regards`
        },
        negotiation: {
          subject: `Success Stories from Companies Like ${deal.companyName}`,
          content: `Hi ${deal.contactName},\n\nI wanted to share some case studies from companies similar to ${deal.companyName} who have seen great results:\n\n[Case Study Links]\n\nLet me know if you have any questions as we finalize the details!\n\nBest regards`
        },
        closed_won: {
          subject: `Welcome to the family, ${deal.companyName}! 🎉`,
          content: `Hi ${deal.contactName},\n\nWelcome aboard! We're thrilled to partner with ${deal.companyName}.\n\nNext steps:\n1. Onboarding kickoff call (scheduling link below)\n2. Account setup\n3. Training sessions\n\nLet's make this a huge success together!\n\nBest regards`
        }
      }

      const template = templates[stageType]
      if (!template) return false

      // Call Firebase Function to send email
      const functions = getFunctions()
      const sendEmailFunction = httpsCallable(functions, 'sendEmailViaZohoSMTP')
      
      const result = await sendEmailFunction({
        to: deal.contactEmail,
        subject: template.subject,
        htmlContent: template.content.replace(/\n/g, '<br>'),
        tenantId: tenantId
      })

      if (result.data.success) {
        // Update deal email stats
        await FirebaseUserDataService.updateDeal(tenantId, deal.id, {
          emailsSent: (deal.emailsSent || 0) + 1,
          lastActivity: new Date().toISOString()
        })
        return true
      }

      return false
    } catch (error) {
      console.error('Error sending stage email:', error)
      return false
    }
  }

  /**
   * Schedule check for deals stuck in stage
   * @param {object} deal - Deal object
   * @param {string} tenantId - Tenant ID
   */
  scheduleStuckDealCheck(deal, tenantId) {
    // In production, this would schedule a Cloud Function
    // For now, we'll check immediately
    if (deal.daysInStage >= 7 && deal.stage !== 'closed_won' && deal.stage !== 'closed_lost') {
      toast(`🚨 Deal going cold: ${deal.companyName} has been in ${deal.stage} for ${deal.daysInStage} days`, {
        duration: 6000,
        icon: '⚠️'
      })

      // Emit stuck deal event
      eventBus.emit(EventTypes.DEAL_STUCK_IN_STAGE, {
        dealId: deal.id,
        stage: deal.stage,
        daysInStage: deal.daysInStage,
        dealValue: deal.dealValue
      })
    }
  }

  /**
   * Helper: Add days to date
   */
  addDaysToDate(date, days) {
    const result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
  }

  /**
   * Helper: Add months to date
   */
  addMonthsToDate(date, months) {
    const result = new Date(date)
    result.setMonth(result.getMonth() + months)
    return result
  }

  /**
   * Play celebration sound
   */
  playCelebrationSound() {
    try {
      // Create audio context and play a simple celebration sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = 800
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.5)
    } catch (error) {
      // Sound playback not critical, fail silently
      console.log('Could not play celebration sound')
    }
  }
}

// Create singleton instance
const dealAutomationEngine = new DealAutomationEngine()

export default dealAutomationEngine
